/**
 * GraphQL Client Configuration
 * Sets up Apollo Client for PX API GraphQL requests
 */

import { InMemoryCache, HttpLink, ApolloLink, CombinedGraphQLErrors } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { ApolloClientV3Compat } from '../core/data/ApolloClientV3Compat';
import { endpoints } from '../core/defaultValues';
import packageJSON from '../../package.json';

/**
 * Get authentication token from localStorage or file (for tests)
 * @returns {string|null} The authentication token
 */
const getAuthToken = () => {
	// Try localStorage first (browser context)
	if (typeof window !== 'undefined' && window.localStorage) {
		return localStorage.getItem('IdToken');
	}
	
	// Fall back to file system (Node.js test context)
	try {
		if (typeof process !== 'undefined') {
			// Use dynamic require to avoid bundling fs in browser build
			// eslint-disable-next-line global-require
			const fs = eval('require')('fs');
			// eslint-disable-next-line global-require
			const path = eval('require')('path');
			const filePath = path.join(process.cwd(), '.idToken');
			if (fs.existsSync(filePath)) {
				return fs.readFileSync(filePath, 'utf8').trim();
			}
		}
	} catch (error) {
		console.warn('Failed to read token from .idToken:', error.message);
	}
	
	return null;
};

/**
 * Auth link to add headers to every request
 */
const authLink = new ApolloLink((operation, forward) => {
	const token = getAuthToken();
	
	operation.setContext({
		headers: {
			authorization: token || '',
			'app-id': 'px-cloud',
			'app-version': packageJSON.version,
			'package-name': packageJSON.name,
		},
	});

	return forward(operation);
});

/**
 * Error link to handle GraphQL errors
 */
const errorLink = new ErrorLink(({ error }) => {
	if (CombinedGraphQLErrors.is(error)) {
		error.errors.forEach(({ message, locations, path, extensions }) => {
			console.error(
				`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
				extensions
			);
		});
	} else if (error) {
		console.error(`[Network error]: ${error}`);
	}
});

/**
 * Create HTTP link with the GraphQL endpoint
 */
const httpLink = new HttpLink({
	uri: endpoints.GRAPHQL_GENERAL_V2,
});

/**
 * Create Apollo Client instance (default - uses GRAPHQL_GENERAL_V2)
 */
const client = new ApolloClientV3Compat({
	link: ApolloLink.from([authLink, errorLink, httpLink]),
	cache: new InMemoryCache({
		typePolicies: {
			Query: {
				fields: {
					// Configure pagination for list types if needed
					searchInspections: {
						keyArgs: ['conditions', 'sort'],
						merge(existing, incoming) {
							return incoming;
						},
					},
					searchDefects: {
						keyArgs: ['conditions', 'sort'],
						merge(existing, incoming) {
							return incoming;
						},
					},
					searchAssets: {
						keyArgs: ['conditions', 'sort'],
						merge(existing, incoming) {
							return incoming;
						},
					},
				},
			},
		},
	}),
	defaultOptions: {
		watchQuery: {
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all',
		},
		query: {
			fetchPolicy: 'network-only',
			errorPolicy: 'all',
		},
		mutate: {
			errorPolicy: 'all',
		},
	},
});

/**
 * Create HTTP link for EVIR endpoint (defects, inspections, inspection builder)
 */
const evirPxEndpoint = endpoints.GRAPHQL_GENERAL_V2;
const evirPxHttpLink = new HttpLink({
	uri: evirPxEndpoint,
});

/**
 * Create Apollo Client instance for EVIR features
 * Uses separate endpoint for defects, inspection history, and inspection builder
 */
export const evirPxClient = new ApolloClientV3Compat({
	link: ApolloLink.from([authLink, errorLink, evirPxHttpLink]),
	cache: new InMemoryCache({
		typePolicies: {
			Query: {
				fields: {
					searchInspections: {
						keyArgs: ['conditions', 'sort'],
						merge(existing, incoming) {
							return incoming;
						},
					},
					searchDefects: {
						keyArgs: ['conditions', 'sort'],
						merge(existing, incoming) {
							return incoming;
						},
					},
					searchRepairs: {
						keyArgs: ['conditions', 'sort'],
						merge(existing, incoming) {
							return incoming;
						},
					},
				},
			},
		},
	}),
	defaultOptions: {
		watchQuery: {
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all',
		},
		query: {
			fetchPolicy: 'network-only',
			errorPolicy: 'all',
		},
		mutate: {
			errorPolicy: 'all',
		},
	},
});

export default client;
